// ABOUTME: SPEC-57 — what a view-toggle tap means and which button is selected. All state lives
// ABOUTME: here (4 useState) so App.js gains none; selection is derived from `reel_state.story`,
// ABOUTME: never a local click flag (R2), so a voice move updates the widget exactly as a tap would.

import { useCallback, useRef, useState } from 'react';
import { CHAPTER_BUTTONS, collectionLabel, returnLabel } from './viewToggleGeometry';
import { anchorCountForChapter } from '../../Projectionist/overlays/chapterTagAnchors';
import { withGesture } from '../../Projectionist/swipeNav/gesturePayload';

const REEL_STAGE = 'reel';
const PIECE_STAGE = 'piece';
const KNOWN_CHAPTER_IDS = new Set(CHAPTER_BUTTONS.map((b) => b.id));
// Literal strings, not an import from useVideoPlayer.js's PlayerState enum — same decoupling
// tradeoff already accepted for `stage` (compared against 'reel'/'piece' literals rather than
// stageReducer's Stage enum): this module stays independent of the locked hook's internals.
const AT_REST_PLAYER_STATES = new Set(['Holding', 'Frozen']);
// Fix 6 (corrected): the outgoing chapter keeps reporting ITS OWN unchanged story for a beat after
// a tap (its exit segment) before the new one ever appears — that repeat is not evidence the tap
// failed, so the echo survives it. This bound is only the fallback for a tap that never resolves
// at all (refused/debounced): generous past any real round trip, short enough not to strand a
// guest on a wrong highlight for long.
const OPTIMISTIC_TIMEOUT_MS = 4000;

/**
 * @param {{ enabled: boolean, stage: string, sendInteraction: Function,
 *           collections?: Array<{key:string,title:string}>|null, reelRailTitle?: string }} deps
 * @returns {{ open: boolean, toggle: Function, buttons: Array, visible: boolean, hasTags: boolean,
 *             observeReelState: (payload: object) => void,
 *             observePendingChapterId: (id: string|null) => void,
 *             observePlayerState: (state: string|null) => void }}
 */
export function useReelViewToggle({
  enabled, stage, sendInteraction, collections = null, reelRailTitle = '',
}) {
  const [open, setOpen] = useState(false);
  const [story, setStory] = useState(null);
  // Mirrors `story` so `observeReelState` can bail before touching state — the hot-path cost is
  // one string comparison per `reel_state` frame; a `setState` at most once per chapter change.
  const storyRef = useRef(null);
  // SPEC-57 fix 2: the SAME pendingChapterId useVideoPlayer computes, forwarded through
  // Projectionist/index.js's onPendingChapterChange (R2 — never a local click flag). Truthy means a
  // hop is in flight; transitionPlanner.js's "newest wins" supersede means a second tap would abandon
  // it and re-plan, visibly retargeting the film mid-cut, so the whole group disables meanwhile.
  const [pendingChapterId, setPendingChapterId] = useState(null);
  // SPEC-57 fix 3: pendingChapterId only covers a multi-hop ROUTE (rare — most taps are one hop
  // and it never sets), so it alone let a guest tap a different chapter while the one they just
  // picked was still mid approach and not yet settled. playerState is the SAME "at rest" signal
  // useVideoPlayer's own defer logic uses (`atRest`, useVideoPlayer.js) — HOLDING or FROZEN.
  const [playerState, setPlayerState] = useState(null);
  // SPEC-57 fix 6 (owner report 2026-09-11, corrected same day): the highlight only ever moved
  // once the real `reel_state` round-trip landed — tap -> network -> decision-engine ->
  // play_chapter -> reel_command back -> the film cuts -> its OWN reel_state report — which reads
  // as a stuck/late highlight even when the tap unambiguously succeeded. This is a client-side
  // ECHO of the tap the guest just made, shown instantly; it never substitutes for the real signal
  // (R1/R2 still own the film and the eventual selection). The first version cleared it on EVERY
  // `observeReelState` call, including the OUTGOING chapter's own tail report (its exit segment
  // still names the old story right after the tap) — that snapped the highlight back to the old
  // chapter and only re-confirmed once the real cut landed, i.e. no visible improvement at all.
  // Corrected: only a genuine story CHANGE clears the echo; a bounded timeout is the fallback for
  // a tap that never resolves (refused/debounced), so it self-corrects without ever hanging.
  const [optimisticChapterId, setOptimisticChapterId] = useState(null);
  const optimisticTimeoutRef = useRef(null);

  const clearOptimisticTimeout = useCallback(() => {
    if (optimisticTimeoutRef.current != null) {
      clearTimeout(optimisticTimeoutRef.current);
      optimisticTimeoutRef.current = null;
    }
  }, []);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  /** Called from App's onPendingChapterChange wiring. Never throws. */
  const observePendingChapterId = useCallback((id) => {
    setPendingChapterId(id ?? null);
  }, []);

  /** Called from App's onPlayerStateChange wiring. Never throws. */
  const observePlayerState = useCallback((state) => {
    setPlayerState(state ?? null);
  }, []);

  /** Called on tap: instant feedback, self-corrected by observeReelState or the timeout below. */
  const setOptimisticGuess = useCallback((id) => {
    clearOptimisticTimeout();
    setOptimisticChapterId(id);
    optimisticTimeoutRef.current = setTimeout(() => {
      optimisticTimeoutRef.current = null;
      setOptimisticChapterId(null);
    }, OPTIMISTIC_TIMEOUT_MS);
  }, [clearOptimisticTimeout]);

  /** Called from App's sendReelState, before the send. Never throws (D-2/§4.9). */
  const observeReelState = useCallback((payload) => {
    const rawStory = payload?.story;
    if (typeof rawStory !== 'string') return;
    // SPEC-61: the mobile colour-roll is its own chapter id on the wire (`collection_roll`,
    // never renamed to `collection` there — see useVideoPlayer.js report()'s own comment), but
    // it IS the "Carousel" view as far as this widget's highlight is concerned. A returning
    // mobile guest can boot straight into the roll (useChapterManifest.js's bootTarget), and
    // without this the raw id matches no CHAPTER_BUTTONS entry — every button read as
    // unselected with no visible reason why (reported 2026-09-15, mobile only: desktop's
    // manifest never sets `rollChapter`, so `next` is always `rawStory` there).
    const next = (payload?.rollChapter && rawStory === payload.rollChapter) ? 'collection' : rawStory;
    // Fix 6 (corrected): the SAME story repeating is the outgoing chapter's own tail, not evidence
    // about a pending tap either way — leave any outstanding echo alone. Only a genuine change
    // clears it, whether it confirms the guess or (a refusal, a voice move elsewhere) overrides it.
    if (next === storyRef.current) return;
    clearOptimisticTimeout();
    setOptimisticChapterId(null);
    if (!KNOWN_CHAPTER_IDS.has(next)) {
      // A manifest rename would otherwise leave every button unselected with no signal why.
      console.warn('[SPEC-57] view toggle: unknown chapter id', next);
    }
    storyRef.current = next;
    setStory(next);
  }, [clearOptimisticTimeout]);

  // Fix 6: what the widget shows as "current" — the echo while one is outstanding, else the real
  // signal. Never persisted, never sent anywhere; `story` (R2) remains the only thing voice moves.
  const displayedStory = optimisticChapterId ?? story;

  const onStage = stage !== REEL_STAGE;
  // SPEC-57 fix 2 (OQ-2 re-decided): a hop in flight disables EVERY button, including the currently
  // selected/disabled one — R3's "selected is inert" is a separate, narrower rule than "a transition
  // is in flight", and the two compose with OR, not replace each other.
  // SPEC-57 fix 3: also disabled until the film has actually settled (HOLDING/FROZEN) — a chapter's
  // approach segment can run several seconds, and R3 alone only inerts the button for the chapter
  // the guest just picked, leaving every OTHER button clickable through that whole animation.
  const transitionPending = pendingChapterId != null
    || (playerState != null && !AT_REST_PLAYER_STATES.has(playerState));

  // R6 fix 5 (owner report 2026-09-11, reverses the original R6 "six chapter buttons stay visible
  // on stage" ruling): on the Vélos stage NONE of the seven chapter buttons render — only the
  // collection/"Vesper" button below. Tapping it is a bare `return_to_reel` (no chapter_id), which
  // already lands back wherever the reel was paused (SPEC-53 pauses/hides the instance in place; it
  // does not reset it) — so "go back to exactly where I was" needs no new logic, only NOT drawing
  // controls for a destination the guest can no longer reach from here in one tap.
  const chapterButtons = onStage ? [] : CHAPTER_BUTTONS.map(({ id, label }) => {
    const selected = id === displayedStory;
    return {
      key: id,
      label,
      selected,
      // R3/D-12 (selected is inert) OR SPEC-57 fix 2 (a hop is already in flight).
      disabled: selected || transitionPending,
      // SPEC-92 D-5: `via` is 'wheel' | 'touch' from a gesture; a click event or a bare call adds nothing.
      onPress: (via) => {
        if (selected || transitionPending) return;
        setOptimisticGuess(id); // fix 6: instant feedback, corrected by the next real signal or timeout
        sendInteraction('chapter_selected', {}, null, withGesture({ chapter_id: id }, via));
      },
    };
  });

  const collection = Array.isArray(collections) && collections.length ? collections[0] : null;
  const collectionButton = collection ? {
    key: `collection:${collection.key}`,
    label: onStage ? returnLabel(reelRailTitle) : collectionLabel(collection.title),
    // SPEC-57 fix 1: never "selected" — unlike a chapter button this is the ONLY way off the Vélos
    // stage, so it must never carry the highlighted/inert "current view" treatment (R3 is for the
    // seven chapter buttons only). The label swap (Vélos <-> Vesper) is the only state it shows.
    selected: false,
    // R6: stays pressable even on stage — see above, this is not R3's disabled-when-selected.
    // SPEC-57 fix 2/3 still apply: a hop in flight or an unsettled film disables it like every
    // other button in the group.
    disabled: transitionPending,
    onPress: (via) => {
      if (transitionPending) return;
      if (onStage) sendInteraction('return_to_reel', {}, null, withGesture({}, via));
      else sendInteraction('enter_collection', {}, null, withGesture({ collection_key: collection.key }, via));
    },
    // SPEC-86 Rev 2: a swipe back from Vélos must land on the carousel's idle lineup, not where the film
    // paused. The existing SPEC-58 shape is `return_to_reel` WITH a chapter_id. Offered on the collection
    // stage only (never the piece stage), so the swipe router needs no stage knowledge of its own.
    ...(onStage && stage !== PIECE_STAGE ? {
      onReturnTo: (chapterId, via) => {
        if (transitionPending) return;
        sendInteraction('return_to_reel', {}, null, withGesture({ chapter_id: chapterId }, via));
      },
    } : {}),
  } : null;

  const buttons = collectionButton ? [...chapterButtons, collectionButton] : chapterButtons;

  // D-6: mobile-only reserve above the point-tag row. Reads the existing anchor table rather than
  // duplicating it — `idle`/`collection` (and any stage, where no tag row exists) resolve to 0.
  const hasTags = !onStage && anchorCountForChapter(story, 'mobile') > 0;

  return {
    open,
    toggle,
    buttons,
    visible: Boolean(enabled) && stage !== PIECE_STAGE, // D-11
    hasTags,
    observeReelState,
    observePendingChapterId,
    observePlayerState,
  };
}

export default useReelViewToggle;
